import type {
  DealerOrderStatusEnum,
  OrderStatusEnum,
  ProductTypeEnum,
  ProviderNameEnum,
  TenantAppModeEnum,
} from '@snowit/models';

// import authTenants from '../system/useTenants/authTenants.json';
import {
  DEVELOPMENT_AUTH_TENANTS,
  PRODUCTION_AUTH_TENANTS,
} from '../system/useTenants/authTenants';

export * from './backend_enums';
export * from './exports';
export * from './statuses';
export * from './tables';
export const NEW_ENTITY = 'new' as const;

export const isNewEntity = (id: unknown): id is string => Boolean(!id || id === NEW_ENTITY);

export const DELETE_KEY = '__DELETE__' as const;
export const NULL_KEY = '__NULL__' as const;
export const UNKNOWN_TENANT = '_UNKNOWN_' as const;

export const cleanDelKey = (input: unknown): string | undefined => {
  return input === DELETE_KEY ? undefined : (input as string);
};

export const LOCALAZIER_URL = 'https://localazier-mb33qx75jq-ew.a.run.app';
// export const LOCALAZIER_URL = 'http://localhost:8080'; // for development

export const APP_BANNERS = [
  'bikeit',
  'black_friday',
  'bracelet',
  'membership',
  'privacy',
  'snowitcard',
  'train',
  'visa',
  'snowcoins',
  'snowcoins_ppu',
  'challenges',
  'snowcoins_renew',
] as const;

/** @deprecated */
export const APP_CUSTOM_HOMEPAGES = ['hotel'] as const;
/** @deprecated */
export const APP_CUSTOM_TUTORIALS = ['hotel'] as const;

export const APP_MODES: TenantAppModeEnum[] = ['full', 'light'];

export const COLLECTION_EXPORT = [
  'Cards',
  'Categories',
  'Coupons',
  'Districts',
  'Conditions',
  'Descriptions',
  'ExternalPrice',
  'Partners',
  'Prices',
  'Products',
  'Options',
  'Stocks',
  'Seasons',
  'Slots',
  'Stages',
  'StageDetails',
  'Variants',
  'Zones',
] as const;

export const COLLECTIONS = [
  'cards',
  'cards~seasonal',
  'carts',
  'categories',
  'challenges',
  'coupons',
  'dealerOrders',
  'discounts',
  'districts',
  'emails',
  'flattenedOrders',
  'forms',
  'galleries',
  'importStatus',
  'notifications',
  'orders',
  'partnerStripes',
  'partners',
  'permissions',
  'prices',
  'prizes',
  'productOptions',
  'productStocks',
  'products',
  'providersOrders',
  'reviews',
  'roles',
  'seasons',
  'sessions',
  'settings',
  'shares',
  'stages',
  'stats',
  'tasks',
  'tenants',
  'venues',
  'zones',
] as const;

// TODO: gli EPP non usano tutti i ProviderName ora. se/quando li useranno tutti va spostato in backend_enums
export const EXTERNAL_PROVIDERS = [
  'alfi',
  'axess',
  'beebeeboard',
  'comunemilano',
  'easyrent',
  'evai',
  'gardaticket',
  'improoving',
  'netstorming',
  'pricenow',
  'ppu~seasonal',
  'quickbooks',
  'spotlio',
  'skidata',
  'skiperformance',
  'skiwork',
  'stripe',
  'tantosvago',
  'trenord',
  'zucchetti',
  'midaticket',
  'bitq',
  'trenitalia',
  'google',
  'getyourguide',
] as const satisfies ReadonlyArray<ProviderNameEnum>;

export const ACCOUNTING_TYPES = [
  'partner_issues_net_of_commissions',
  'partner_issues_sold_sportit_issues_commissions',
  'sportit_issues_net_of_commissions',
  'other',
] as const;

export const ACCOUNTING_TIME = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'seasonal',
  'other',
] as const;

export const ACCOUNTING_TOOL = ['stripe_connect', 'ixaris', 'transfer', 'other'] as const;

export const HOTELS_FILTERS = [
  {
    key: 'AVAILONLY',
    label:
      'returns only the hotel agreements on immediate confirmation (.../agreement[@available]=true)',
  },
  {
    key: 'AVLHEAVY',
    label:
      'In the price comparison the system gives higher weight to the hotel agreements on immediate confirmation. On reques results will be returned only if the rate is much more convenient.',
  },
  {
    key: 'BESTPRICE',
    label:
      'returns only the contract with the best price for each hotel (.../agreement[@total]=min per hotel)',
  },
  {
    key: 'BESTCONTRACT',
    label:
      'returns the best price for each contract type of the hotels (.../agreement[@total]=min per .../agreement[@c_type]). Dynamic inventory (.../agreement[@is_dynamic]=true) is excluded from this comparison',
  },
  {
    key: 'BESTROOMBASIS',
    label:
      'returns the best price for each room basis (.../agreement[@total]=min per .../agreement[@room_basis]), regardless of the contract types. WARNING: right now this filter is available only in live environment',
  },
  {
    key: 'EXCLUDENONREF',
    label:
      'the system will remove from the response all the contract typologies - statically mapped or dynamically achieved - containing the typical "non refundable" wording in their description',
  },
  {
    key: 'EXCLUDEDYNAMIC',
    label:
      'the system will remove from the response all the dynamic contracts (.../agreement[@is_dynamic]=true)',
  },
  {
    key: 'REDUCED',
    label:
      'the system will remove from the output a number of fields for hotel details and agreements which are not mandatory',
  },
  {
    key: 'BESTARRANGMENT',
    label:
      '(default): returns the best price for each different arrangement type (unique pairs of.../agreement[@c_type] and .../agreement[@room_basis] ) of the hotels',
  },
] as const;

export const ALLOWED_ORDER_STATUS = [
  'ok',
  'fulfilled',
  'canceled',
] as const satisfies ReadonlyArray<OrderStatusEnum>;

export const EDITABLE_ORDER_ITEM_STATUS: OrderStatusEnum[] = ['ok', 'fulfilled'];

export const CANCELLABLE_ORDER_ITEM_STATUS: OrderStatusEnum[] = ['ok', 'fulfilled'];

export const CANCELLABLE_ORDER_STATUS: OrderStatusEnum[] = ['ok', 'fulfilled', 'on-hold'];

export const ALLOWED_DEALER_STATUS: DealerOrderStatusEnum[] = ['delivered', 'pending'];

// inverted order
export const SORT_TENANTS = ['bikeit', 'snowit~summer', 'snowit-travel', 'snowit'];
export const SORT_TENANTS_2 = ['default', 'snowit', 'discovera', 'tribala', 'bikeit'];

export const AUTH_TENANTS: readonly string[] =
  process.env.SNOWIT_ENVIRONMENT === 'production'
    ? PRODUCTION_AUTH_TENANTS
    : DEVELOPMENT_AUTH_TENANTS;

export const PRODUCTS_WITH_EXTRADATA: ProductTypeEnum[] = [
  'bundle~travel',
  // 'bundle~train',
  // 'hotel',
  'itinerary',
  'transport',
];

export const PRODUCTS_VARIANT_GROUPS: ProductTypeEnum[] = ['bundle~travel', 'bundle~train'];
export const PRODUCTS_MEMBERSHIPS: ProductTypeEnum[] = [
  'skipass~membership',
  'skipass~membership-seasonal',
];
